/**
 * ИИ-боты, которые проверяет инструмент.
 *
 * `ua` — настоящая строка User-Agent бота: с ней делается живой заход,
 * чтобы увидеть, что отвечает сайт, а не только что написано в robots.txt.
 * Там, где `ua` нет, бот существует только как имя в robots.txt
 * (Google-Extended, Applebot-Extended): отдельного обходчика у них нет.
 *
 * Назначение важно для совета: закрыть обучение — законный выбор владельца,
 * а закрыть поиск ChatGPT или Perplexity значит пропасть из их ответов.
 */

export type BotPurpose = 'search' | 'user' | 'training' | 'classic';

export interface AiBot {
  token: string;
  owner: string;
  purpose: BotPurpose;
  ua?: string;
}

export const AI_BOTS: readonly AiBot[] = [
  {
    token: 'OAI-SearchBot',
    owner: 'OpenAI',
    purpose: 'search',
    ua: 'Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; OAI-SearchBot/1.0; +https://openai.com/searchbot',
  },
  {
    token: 'ChatGPT-User',
    owner: 'OpenAI',
    purpose: 'user',
    ua: 'Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; ChatGPT-User/1.0; +https://openai.com/bot',
  },
  {
    token: 'GPTBot',
    owner: 'OpenAI',
    purpose: 'training',
    ua: 'Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; GPTBot/1.1; +https://openai.com/gptbot',
  },
  {
    token: 'Claude-SearchBot',
    owner: 'Anthropic',
    purpose: 'search',
    ua: 'Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; Claude-SearchBot/1.0; +https://www.anthropic.com',
  },
  {
    token: 'Claude-User',
    owner: 'Anthropic',
    purpose: 'user',
    ua: 'Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; Claude-User/1.0; +https://www.anthropic.com',
  },
  {
    token: 'ClaudeBot',
    owner: 'Anthropic',
    purpose: 'training',
    ua: 'Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; ClaudeBot/1.0; +claudebot@anthropic.com',
  },
  {
    token: 'PerplexityBot',
    owner: 'Perplexity',
    purpose: 'search',
    ua: 'Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; PerplexityBot/1.0; +https://perplexity.ai/perplexitybot)',
  },
  {
    token: 'Perplexity-User',
    owner: 'Perplexity',
    purpose: 'user',
    ua: 'Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; Perplexity-User/1.0; +https://perplexity.ai/perplexity-user)',
  },
  { token: 'Google-Extended', owner: 'Google (Gemini)', purpose: 'training' },
  { token: 'Applebot-Extended', owner: 'Apple', purpose: 'training' },
  {
    token: 'CCBot',
    owner: 'Common Crawl',
    purpose: 'training',
    ua: 'CCBot/2.0 (https://commoncrawl.org/faq/)',
  },
  {
    token: 'Bytespider',
    owner: 'ByteDance',
    purpose: 'training',
    ua: 'Mozilla/5.0 (Linux; Android 5.0) AppleWebKit/537.36 (KHTML, like Gecko) Mobile Safari/537.36 (compatible; Bytespider; spider-feedback@bytedance.com)',
  },
  {
    token: 'meta-externalagent',
    owner: 'Meta',
    purpose: 'training',
    ua: 'meta-externalagent/1.1 (+https://developers.facebook.com/docs/sharing/webmasters/crawler)',
  },
  // Обычные поисковики: без них сайт не найдут и классическим поиском,
  // а по их доступу видно, что закрыт именно ИИ, а не всё подряд.
  { token: 'Googlebot', owner: 'Google Search', purpose: 'classic' },
  { token: 'Bingbot', owner: 'Bing (and Copilot)', purpose: 'classic' },
];

/**
 * Готовый кусок robots.txt: перечисленным ботам закрыт весь сайт.
 *
 * Только группы Disallow. Своя группа бота заменяет для него группу `*`
 * целиком, поэтому группа `Allow: /` для «разрешённого» бота открыла бы
 * ему и то, что владелец закрыл для всех (/admin, /cart). Разрешённые
 * боты остаются под общими правилами сайта, и кусок безопасно вставлять
 * в начало существующего файла. Пустой список даёт пустую строку.
 */
export function robotsSnippet(blocked: readonly string[]): string {
  const lines: string[] = [];
  for (const bot of AI_BOTS) {
    if (bot.purpose === 'classic' || !blocked.includes(bot.token)) continue;
    lines.push(`User-agent: ${bot.token}`);
  }
  if (lines.length === 0) return '';
  lines.push('Disallow: /');
  return lines.join('\n') + '\n';
}

/** Готовые наборы: кого закрыть. */
export const PRESETS = {
  allowAll: [] as string[],
  searchOnly: AI_BOTS.filter((b) => b.purpose === 'training').map((b) => b.token),
  blockAll: AI_BOTS.filter((b) => b.purpose !== 'classic').map((b) => b.token),
} as const;
